'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { usePrefixedNavigate } from '@/lib/auth/usePrefixedNavigate'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/store/hooks'
import { selectUser } from '@/store/slices/authSlice'
import { env } from '@/env'
import { UserAvatar, useUserProfile } from '@/modules/user-profile'
import { useCurrentUserRole } from '@/modules/admin'

export interface UserProfileButtonProps {
  expanded?: boolean
}

export function UserProfileButton({
  expanded = false,
}: UserProfileButtonProps) {
  const { t } = useTranslation('admin')
  const [isOpen, setIsOpen] = React.useState(false)
  const [isMounted, setIsMounted] = React.useState(false)
  const navigate = usePrefixedNavigate()
  const { logout } = useAuth()
  const user = useAppSelector(selectUser)

  const { data: userProfile } = useUserProfile()
  const avatarUrl = userProfile?.pictureUrl ?? undefined

  const name = userProfile?.fullName || user?.name || 'User'
  const email = user?.email || ''

  // Defer dropdown rendering until after hydration to avoid ID mismatch
  React.useEffect(() => {
    setIsMounted(true)
  }, [])

  const handleUserProfile = () => {
    navigate('/profile')
    setIsOpen(false)
  }

  const { isAdmin } = useCurrentUserRole()

  const handleAdmin = () => {
    navigate('/admin')
    setIsOpen(false)
  }

  const handleLogout = async () => {
    await logout()
  }

  const buttonContent = (
    <>
      <UserAvatar
        key={userProfile?.profilePictureId ?? user?.sub ?? 'user'}
        src={avatarUrl}
        name={name}
        size="lg"
        className={cn(
          "shrink-0 border-border-primary uppercase transition-transform duration-300",
          !expanded && "group-hover:scale-105 hover:border-border-secondary"
        )}
        backgroundColor="bg-red-100"
      />
      <div
        className={cn(
          'flex flex-col items-start overflow-hidden whitespace-nowrap',
          'transition-all duration-200 ease-in-out',
          expanded ? 'opacity-100 max-w-[200px]' : 'opacity-0 max-w-0'
        )}
        aria-hidden={!expanded}
      >
        <span className="body-sm-medium text-text-primary truncate max-w-[120px]">
          {name}
        </span>
        <span className="body-xs-regular text-text-tertiary truncate max-w-[120px]">
          {email}
        </span>
        <i className={cn(
          "pi pi-chevron-down text-xs text-text-tertiary absolute right-4 top-1/2 -translate-y-1/2 transition-transform duration-200",
          isOpen ? "rotate-180" : "rotate-0"
        )} />
      </div>
    </>
  )

  const buttonClassName = cn(
    'group flex relative items-center justify-start w-full',
    'h-[48px] gap-3 translate-x-1',
    'rounded-(--sidebar-link-radius)',
    'transition-all duration-200 ease-in-out',
    expanded && 'hover:bg-(--sidebar-link-bg-hover)'
  )

  // Render static button during SSR to avoid hydration mismatch
  if (!isMounted) {
    return (
      <button
        type="button"
        className={buttonClassName}
        title="User Profile"
      >
        {buttonContent}
      </button>
    )
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={buttonClassName}
          title="User Profile"
        >
          {buttonContent}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-[236px]">

        <div
          className="px-2 py-2 text-xs text-text-quaternary text-left select-none"
          title={`Built: ${env.APP_VERSION} ${__BUILD_TIME__}`}
        >
          {env.APP_VERSION === 'local'
            ? `Version ${env.APP_VERSION} (${__BUILD_HASH__})`
            : `Version ${env.APP_VERSION}`}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleUserProfile} className="gap-2 py-3">
          <i className="pi pi-user text-sm text-text-secondary" aria-hidden="true" />
          User profile
        </DropdownMenuItem>
        {isAdmin && (
          <DropdownMenuItem onClick={handleAdmin} className="gap-2 py-3">
            <i className="pi pi-shield text-sm text-text-secondary" aria-hidden="true" />
            {t('sidebar.admin')}
          </DropdownMenuItem>
        )}

        <DropdownMenuItem onClick={handleLogout} className="gap-2 py-3">
          <i className="pi pi-sign-out text-sm text-text-secondary" aria-hidden="true" />
          Logout
        </DropdownMenuItem>

      </DropdownMenuContent>
    </DropdownMenu>
  )
}
